import { UserShortResponse } from "../../../../../api";
import {
  EMPTY_ATTORNEYS_FILTERS,
  YES_NO_OPTIONS,
  type AttorneysFiltersState,
} from "./types";

export function countActiveFilters(filters: AttorneysFiltersState): number {
  let count = 0;
  if (filters.practiceArea.trim()) count += 1;
  if (filters.emailDomain.trim()) count += 1;
  if (filters.hasCompany.trim()) count += 1;
  return count;
}

export function hasActiveFilters(filters: AttorneysFiltersState): boolean {
  return countActiveFilters(filters) > 0;
}

export function clearFilterField(
  filters: AttorneysFiltersState,
  key: keyof AttorneysFiltersState
): AttorneysFiltersState {
  return { ...filters, [key]: "" };
}

export function resetFilters(): AttorneysFiltersState {
  return { ...EMPTY_ATTORNEYS_FILTERS };
}

function getEmailDomain(email?: string | null): string {
  if (!email) return "";
  const at = email.lastIndexOf("@");
  if (at < 0 || at === email.length - 1) return "";
  return email.slice(at + 1).toLowerCase();
}

export function extractEmailDomains(attorneys: UserShortResponse[]): string[] {
  const domains = new Set<string>();
  attorneys.forEach((attorney) => {
    const domain = getEmailDomain(attorney.email);
    if (domain) domains.add(domain);
  });
  return Array.from(domains).sort((a, b) => a.localeCompare(b));
}

export function matchesAttorneysFilters(
  attorney: UserShortResponse,
  filters: AttorneysFiltersState
): boolean {
  if (filters.practiceArea && attorney.practiceArea !== filters.practiceArea) {
    return false;
  }

  if (filters.emailDomain) {
    const domain = getEmailDomain(attorney.email);
    if (domain !== filters.emailDomain.toLowerCase()) {
      return false;
    }
  }

  const hasCompany = Boolean(attorney.companyName?.trim());
  if (filters.hasCompany === "yes" && !hasCompany) {
    return false;
  }
  if (filters.hasCompany === "no" && hasCompany) {
    return false;
  }

  return true;
}

export function buildActiveChips(
  filters: AttorneysFiltersState,
  formatPracticeArea: (value: string) => string
): { key: keyof AttorneysFiltersState; label: string }[] {
  const chips: { key: keyof AttorneysFiltersState; label: string }[] = [];

  if (filters.practiceArea) {
    chips.push({
      key: "practiceArea",
      label: `Practice area: ${formatPracticeArea(filters.practiceArea)}`,
    });
  }
  if (filters.emailDomain) {
    chips.push({
      key: "emailDomain",
      label: `Email domain: ${filters.emailDomain}`,
    });
  }
  if (filters.hasCompany) {
    const companyLabel =
      YES_NO_OPTIONS.find((o) => o.value === filters.hasCompany)?.label ??
      filters.hasCompany;
    chips.push({
      key: "hasCompany",
      label: `Has company: ${companyLabel}`,
    });
  }

  return chips;
}
